/**
 * React Query hooks for Events
 * 
 * Standard pattern for event data fetching and mutations.
 * Reference implementation for Issue #178.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  eventsApi, 
  type EventFilterParams,
  type CreateEventPayload, 
  type UpdateEventPayload, 
  type RSVPPayload 
} from '../lib/api/events';
import { useClubs } from './useClubs';

/**
 * Fetch all events across all clubs the user is a member of
 * 
 * Query Key: ['events', 'my-clubs']
 * Returns: Array of events with RSVP info from user's clubs
 * 
 * Future: Will include nearby public races and events
 */
export function useAllEvents() {
  const { data: clubs } = useClubs();
  
  return useQuery({
    queryKey: ['events', 'my-clubs', clubs?.map(club => club.id).sort()],
    queryFn: async () => {
      if (!clubs || clubs.length === 0) {
        return [];
      }
      
      // Fetch events from all user's clubs in parallel
      const eventPromises = clubs.map(club => 
        eventsApi.getClubEvents(club.id)
      );
      
      const results = await Promise.all(eventPromises);
      
      // Flatten and sort by start date
      return results
        .flat()
        .sort((a, b) => a.starts_at - b.starts_at);
    },
    enabled: !!clubs && clubs.length > 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch events for a specific club with optional date filtering
 * 
 * Query Key: ['events', { clubId, from?, to? }]
 * Returns: Array of events with RSVP info
 * 
 * Note: Backend supports from/to timestamp filtering via query params
 */
export function useClubEvents(clubId: string, filter?: EventFilterParams) {
  return useQuery({
    queryKey: ['events', { clubId, ...filter }],
    queryFn: () => eventsApi.getClubEvents(clubId, filter),
    enabled: !!clubId,
    staleTime: 1000 * 60 * 2, // 2 minutes - events don't change that frequently
  });
}

/**
 * Fetch single event by ID
 * 
 * Query Key: ['event', eventId]
 * Returns: Event details with RSVP status
 */
export function useEvent(eventId: string, options?: { staleTime?: number }) {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: async () => {
      const response = await eventsApi.getEvent(eventId);
      return response.event; // Extract the event from the wrapper
    },
    enabled: !!eventId,
    staleTime: options?.staleTime ?? 0, // Default: Always refetch to avoid stale 401 errors
    retry: 1, // Retry once on error
  });
}

/**
 * Create a new event
 * 
 * On success: Invalidates ['events'] queries to refresh lists
 */
export function useCreateEvent(clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: CreateEventPayload) => eventsApi.createEvent(clubId, payload),
    onSuccess: () => {
      // Refresh events list for this club
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
    },
  });
}

/**
 * Update event details
 * 
 * On success: Invalidates both event list and specific event query
 */
export function useUpdateEvent(eventId: string, clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: UpdateEventPayload) => eventsApi.updateEvent(eventId, payload),
    onSuccess: () => {
      // Refresh both the list and the specific event
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
    },
  });
}

/**
 * Delete an event
 * 
 * On success: Invalidates event list queries
 */
export function useDeleteEvent(clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (eventId: string) => eventsApi.deleteEvent(eventId),
    onSuccess: () => {
      // Refresh events list
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
    },
  });
}

/**
 * RSVP to an event
 * 
 * On success: Invalidates both event list and specific event to refresh RSVP counts
 */
export function useEventRSVP(eventId: string, clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (payload: RSVPPayload) => eventsApi.rsvpToEvent(eventId, payload),
    onSuccess: () => {
      // Refresh both the list (for RSVP counts) and the specific event
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
    },
  });
}

/**
 * Upload event hero image
 * 
 * On success: Invalidates event queries to refresh image data
 */
export function useUploadEventHeroImage(eventId: string, clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (file: File) => eventsApi.uploadEventHeroImage(eventId, file),
    onSuccess: () => {
      // Refresh both the specific event and event lists to show new image
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['events', 'my-clubs'] 
      });
    },
  });
}

/**
 * Remove event hero image
 * 
 * On success: Invalidates event queries to refresh fallback image
 */
export function useRemoveEventHeroImage(eventId: string, clubId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => eventsApi.removeEventHeroImage(eventId),
    onSuccess: () => {
      // Refresh both the specific event and event lists to show fallback image
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['events', { clubId }] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['events', 'my-clubs'] 
      });
    },
  });
}

/**
 * Fetch clubs where the user can create events (admin or owner)
 * 
 * Query Key: ['clubs-for-event-creation']
 * Returns: Array of clubs where user has admin or owner privileges
 */
export function useClubsForEventCreation() {
  return useQuery({
    queryKey: ['clubs-for-event-creation'],
    queryFn: eventsApi.getClubsForEventCreation,
    staleTime: 1000 * 60 * 5, // 5 minutes - club leadership doesn't change frequently
  });
}
